/**
 * Parallax Effect Module
 * 
 * @package Lastag
 * @version 1.0.0
 */

class Parallax {
    constructor(options = {}) {
        this.options = {
            speed: 0.5,
            threshold: 0.1,
            ...options
        };
        
        this.elements = [];
        this.isScrolling = false;
        this.observer = null;
        this.lastScrollY = window.pageYOffset;
        
        this.handleScroll = this.throttledScroll.bind(this);
        this.handleResize = this.debounce(this.updateElementPositions.bind(this), 250);
    }

    init() {
        this.findParallaxElements();
        this.setupIntersectionObserver();
        this.bindEvents();
        this.updateElementPositions();
    }

    findParallaxElements() {
        const elements = document.querySelectorAll('.number-counter-section, [data-parallax]');
        
        elements.forEach(element => {
            const speed = parseFloat(element.getAttribute('data-parallax-speed')) || this.options.speed;
            const direction = element.getAttribute('data-parallax-direction') || 'up';
            const type = element.getAttribute('data-parallax-type') || 'background';
            const target = element.getAttribute('data-parallax-target');
            const intensity = parseFloat(element.getAttribute('data-parallax-intensity')) || 1;
            
            this.elements.push({
                element,
                speed,
                direction,
                type,
                target,
                intensity,
                isVisible: false,
                rect: element.getBoundingClientRect(),
                initialTransform: 0
            });
        });
    }

    setupIntersectionObserver() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const parallaxData = this.elements.find(item => item.element === entry.target);
                if (parallaxData) {
                    parallaxData.isVisible = entry.isIntersecting;
                }
            });
        }, { 
            threshold: this.options.threshold,
            rootMargin: '50px 0px'
        });

        this.elements.forEach(({ element }) => {
            this.observer.observe(element);
        });
    }

    bindEvents() {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize, { passive: true });
    }

    throttledScroll() {
        if (!this.isScrolling) {
            requestAnimationFrame(() => {
                this.updateParallax();
                this.isScrolling = false;
            });
            this.isScrolling = true;
        }
    }

    updateParallax() {
        const scrollY = window.pageYOffset;
        const scrollDirection = scrollY > this.lastScrollY ? 'down' : 'up';
        this.lastScrollY = scrollY;

        this.elements.forEach(({ element, speed, direction, type, target, intensity, isVisible }) => {
            if (!isVisible) return;

            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + scrollY;
            const elementHeight = rect.height;
            const viewportHeight = window.innerHeight;

            const elementCenter = elementTop + elementHeight / 2;
            const viewportCenter = scrollY + viewportHeight / 2;
            const distance = elementCenter - viewportCenter;
            let offset = distance * speed * intensity;
            
            if (direction === 'down') {
                offset = -offset;
            }

            const targetElement = target ? element.querySelector(target) : element;
            
            if (!targetElement) return;

            switch (type) {
                case 'background':
                    this.applyBackgroundParallax(element, offset, scrollY, speed);
                    break;
                    
                case 'transform':
                    this.applyTransformParallax(targetElement, offset);
                    break;
                    
                case 'scale':
                    this.applyScaleParallax(targetElement, offset, intensity);
                    break;
                    
                case 'rotate':
                    this.applyRotateParallax(targetElement, offset, intensity);
                    break;
                    
                default:
                    this.applyBackgroundParallax(element, offset, scrollY, speed);
            }
        });
    }

    updateElementPositions() {
        this.elements.forEach(item => {
            item.rect = item.element.getBoundingClientRect();
        });
        this.updateParallax();
    }

    /**
     * Apply background parallax effect
     * @param {HTMLElement} element - Element to apply background parallax to
     * @param {number} offset - Parallax offset
     * @param {number} scrollY - Current scroll position
     * @param {number} speed - Parallax speed
     */
    applyBackgroundParallax(element, offset, scrollY, speed) {
        const transform = `translate3d(0, ${offset}px, 0)`;
        element.style.setProperty('--parallax-transform', transform);

        if (element.classList.contains('number-counter-section')) {
            const bgOffset = scrollY * speed * 0.3;
            element.style.setProperty('--bg-position-y', `${50 + bgOffset}%`);
        }
    }

    /**
     * Apply transform parallax effect
     * @param {HTMLElement} element - Element to apply transform parallax to
     * @param {number} offset - Parallax offset
     */
    applyTransformParallax(element, offset) {
        const transform = `translate3d(0, ${offset}px, 0)`;
        element.style.transform = transform;
        element.style.willChange = 'transform';
    }

    /**
     * Apply scale parallax effect
     * @param {HTMLElement} element - Element to apply scale parallax to
     * @param {number} offset - Parallax offset
     * @param {number} intensity - Effect intensity
     */
    applyScaleParallax(element, offset, intensity) {
        const scale = 1 + (offset * intensity * 0.001);
        const boundedScale = Math.max(0.5, Math.min(2, scale));
        const transform = `scale(${boundedScale})`;
        element.style.transform = transform;
        element.style.willChange = 'transform';
    }

    /**
     * Apply rotate parallax effect
     * @param {HTMLElement} element - Element to apply rotate parallax to
     * @param {number} offset - Parallax offset
     * @param {number} intensity - Effect intensity
     */
    applyRotateParallax(element, offset, intensity) {
        const rotation = offset * intensity * 0.1;
        const transform = `rotate(${rotation}deg)`;
        element.style.transform = transform;
        element.style.willChange = 'transform';
    }

    /**
     * Debounce utility function
     * @param {Function} func - Function to debounce
     * @param {number} delay - Delay in milliseconds
     */
    debounce(func, delay) {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    }

    /**
     * Add new parallax element dynamically
     * @param {HTMLElement} element - Element to add parallax to
     * @param {Object} options - Parallax options
     */
    addElement(element, options = {}) {
        const speed = options.speed || this.options.speed;
        const direction = options.direction || 'up';
        const type = options.type || 'background';
        const target = options.target || null;
        const intensity = options.intensity || 1;

        const parallaxData = {
            element,
            speed,
            direction,
            type,
            target,
            intensity,
            isVisible: false,
            rect: element.getBoundingClientRect(),
            initialTransform: 0
        };

        this.elements.push(parallaxData);
        this.observer.observe(element);
    }

    /**
     * Remove parallax element
     * @param {HTMLElement} element - Element to remove
     */
    removeElement(element) {
        this.elements = this.elements.filter(item => item.element !== element);
        this.observer.unobserve(element);
    }

    /**
     * Cleanup and destroy parallax instance
     */
    destroy() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        
        if (this.observer) {
            this.observer.disconnect();
        }

        this.elements.forEach(({ element, target }) => {
            element.style.removeProperty('--parallax-transform');
            element.style.removeProperty('--bg-position-y');
            
            const targetElement = target ? element.querySelector(target) : element;
            if (targetElement && targetElement !== element) {
                targetElement.style.removeProperty('transform');
                targetElement.style.removeProperty('will-change');
            }
        });

        this.elements = [];
    }
}

const parallaxInstance = new Parallax();

function initializeParallax(options = {}) {
    if (options && Object.keys(options).length > 0) {
        const customParallax = new Parallax(options);
        customParallax.init();
        return customParallax;
    }
    
    parallaxInstance.init();
    return parallaxInstance;
}

export { Parallax, initializeParallax };

if (typeof window !== 'undefined') {
    window.LastagParallax = Parallax;
    window.initializeParallax = initializeParallax;
}